import { i18n } from '@/plugins/i18n';
import { BREAKPOINTS } from '@/types/breakpoints';

const MIN_DISPLAYABLE_DATE_YEAR = 2000;

export default {
  /**
   * Check if the move is international (outside France)
   * @param {Object} demand - Demand object with address information
   * @returns {boolean} True if move is international
   */
  isInternational(demand: {
    from_address: {
      country_code: string;
    };
    to_address: {
      country_code: string;
    };
  }) {
    const fromCountry = demand.from_address.country_code;
    const toCountry = demand.to_address.country_code;
    return (!!fromCountry && fromCountry !== 'FR')
      || (!!toCountry && toCountry !== 'FR');
  },

  /**
   * Format final loading dates with fallback to offer or demand dates
   * @param {Object} demand - Demand object
   * @param {Object} offer - Offer object
   * @param {string} preferredFormat - Date format preference
   * @returns {string} Formatted date string
   */
  formatFinalLoadingDates(demand: {
    dep_date1: string;
    dep_date2: string;
    final_dep_date_from: string;
    final_dep_date_to: string;
  }, offer?: {
    load1_date_from: string;
    load1_date_to: string;
  }, preferredFormat?: string) {
    preferredFormat = preferredFormat || '';
    if (demand?.final_dep_date_from) {
      return this.formatDates(
        demand.final_dep_date_from,
        demand.final_dep_date_to,
        preferredFormat,
      );
    }
    if (offer?.load1_date_from) {
      return this.formatDates(
        offer.load1_date_from,
        offer.load1_date_to,
        preferredFormat,
      );
    }
    return this.formatDates(
      demand.dep_date1,
      demand.dep_date2,
      preferredFormat,
    );
  },

  formatFinalDeliveryDates(demand: {
    arr_date1: string;
    arr_date2: string;
    final_arr_date_from: string;
    final_arr_date_to: string;
  }, offer?: {
    unload1_date_from: string;
    unload1_date_to: string;
    }, preferredFormat?: string) {
    preferredFormat = preferredFormat || '';
    if (demand?.final_arr_date_from) {
      return this.formatDates(
        demand.final_arr_date_from,
        demand.final_arr_date_to,
        preferredFormat,
      );
    }
    if (offer?.unload1_date_from) {
      return this.formatDates(
        offer.unload1_date_from,
        offer.unload1_date_to,
        preferredFormat,
      );
    }
    return this.formatDates(
      demand.arr_date1,
      demand.arr_date2,
      preferredFormat,
    );
  },

  /**
   * Format a date object to string with specified format
   * @param {Date | string} d - Date object (UTC formatting) or date string (local formatting)
   * @param {string} formatType - Format type ('short' or full)
   * @returns {string} Formatted date string
   */
  formatDate(d: Date | string, formatType: string = 'full', useUTC: boolean = true) {
    const isDateString = typeof d === 'string';
    const date = isDateString ? new Date(d) : d;

    const dayNumber = useUTC ? date.getUTCDate() : date.getDate();
    const monthNumber = useUTC ? (date.getUTCMonth() + 1) : (date.getMonth() + 1);
    const yearNumber = useUTC ? date.getUTCFullYear() : date.getFullYear();

    const day = dayNumber < 10 ? `0${dayNumber}` : `${dayNumber}`;
    const month = monthNumber < 10 ? `0${monthNumber}` : `${monthNumber}`;

    if (formatType === 'short') return `${day}/${month}`;
    return `${day}/${month}/${yearNumber}`;
  },

  parseDisplayableDate(dateString: string) {
    if (!dateString) {
      return null;
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime()) || date.getUTCFullYear() < MIN_DISPLAYABLE_DATE_YEAR) {
      return null;
    }

    return date;
  },

  formatBetweenDates(dateFromString: string, dateToString: string, preferredFormat: string) {
    const dateFrom = this.parseDisplayableDate(dateFromString);
    if (!dateFrom) {
      return '';
    }
    const dateTo = this.parseDisplayableDate(dateToString);
    if (dateTo && dateTo > dateFrom && dateToString !== dateFromString) {
      return i18n.global.t('comparator.offers.header.between', {
        date1: this.formatDate(dateFrom, preferredFormat),
        date2: this.formatDate(dateTo, preferredFormat),
      });
    }
    return i18n.global.t('comparator.offers.header.from', {
      date: this.formatDate(dateFrom, preferredFormat),
    });
  },

  formatDates(dateFromString: string, dateToString: string, preferredFormat?: string) {
    preferredFormat = preferredFormat || '';
    const dateFrom = this.parseDisplayableDate(dateFromString);
    if (!dateFrom) {
      return '';
    }
    const dateTo = this.parseDisplayableDate(dateToString);
    if (dateTo && dateTo > dateFrom && dateToString !== dateFromString) {
      if (BREAKPOINTS.tablet < window.innerWidth) {
        return i18n.global.t('comparator.offers.header.fromToLong', {
          date1: this.formatDate(dateFrom, preferredFormat),
          date2: this.formatDate(dateTo, preferredFormat),
        });
      }
      return i18n.global.t('comparator.offers.header.fromToShort', {
        date1: this.formatDate(dateFrom, preferredFormat ?? 'short'),
        date2: this.formatDate(dateTo, preferredFormat ?? 'short'),
      });
    }
    if (BREAKPOINTS.tablet > window.innerWidth) {
      return this.formatDate(dateFrom, preferredFormat ?? 'short');
    }
    return i18n.global.t('comparator.offers.header.from', {
      date: this.formatDate(dateFrom, preferredFormat),
    });
  },

  formatDateString(options: Intl.DateTimeFormatOptions = {}, date: {
    year?: number;
    month?: number;
    date?: number;
    hour?: number;
    minutes?: number;
    seconds?: number;
  } = {}, lang = 'fr') {
    const finalOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      ...options,
    };
    const safeDate = {
      year: date.year ?? 1999,
      month: date.month ?? 1,
      date: date.date ?? 1,
      hour: date.hour ?? 1,
      minutes: date.minutes ?? 1,
      seconds: date.seconds ?? 0,
    };
    const newDate = new Date(Date.UTC(
      safeDate.year,
      safeDate.month,
      safeDate.date,
      safeDate.hour,
      safeDate.minutes,
      safeDate.seconds,
    ));
    return newDate.toLocaleDateString(lang, finalOptions);
  },

  /**
   * Format day count to human-readable string
   * @param {number} day - Number of days
   * @returns {string} Formatted day string (hours or days)
   */
  formatDay(day: number) {
    return day <= 2 ? (`${day * 24}h`) : (`${day} jours`);
  },

  dateFixe(dateFrom: string, dateTo: string) {
    if ((!dateFrom && dateTo) || (dateFrom && !dateTo) || (dateFrom === dateTo)) {
      return this.formatDate(new Date(dateFrom || dateTo));
    }
    return '';
  },
};
